import { Alert, AlertIcon, Card, CardBody, Image } from "@chakra-ui/react";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import images from "../../assets/img";
import LoginForm, { LoginFormValues } from "../../components/forms/LoginForm";
import useConfirmEmail from "../../hooks/api/auth/useConfirmEmail";
import useLogin from "../../hooks/api/auth/useLogin";
import { Jwt } from "../../models/Jwt";
import useAuthState from "../../state/auth.state";
import BasePage from "./BasePage";

export default function LoginPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { mutateAsync: login, isPending: isLoginLoading } = useLogin();
  const {
    mutateAsync: confirmEmail,
    isPending: isConfirmEmailLoading,
    isSuccess: isEmailConfirmed,
    isError: isEmailConfirmationError,
  } = useConfirmEmail();
  const navigate = useNavigate();
  const { setAuth } = useAuthState();
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      confirmEmail({ token });
    }
  }, [confirmEmail, searchParams]);

  const onSubmit = useCallback(
    (credentials: LoginFormValues) => {
      setErrorMessage("");
      login({ credentials })
        .then((loginResponse) => {
          const role = jwtDecode<Jwt>(loginResponse.token).authorities[0]
            .authority;

          setAuth(loginResponse, role);

          if (role === "ROLE_STUDENT") {
            navigate("/student");
          } else if (role === "ROLE_TEACHER") {
            navigate("/teacher");
          } else {
            navigate("/admin");
          }
        })
        .catch(() => setErrorMessage(t("login_page.invalid_credentials")));
    },
    [login, navigate, setAuth, t],
  );

  return (
    <BasePage
      hasBoxWrapper={false}
      withBreadcrumb={false}
      containerProps={{
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
      }}
    >
      <Card maxW="md" mb={12} flexGrow={1}>
        <CardBody display="flex" justifyContent="center" flexDirection="column">
          {isEmailConfirmationError && (
            <Alert mb={2} status="error">
              <AlertIcon />
              {t("login_page.email_not_confirmed")}
            </Alert>
          )}
          {isEmailConfirmed && (
            <Alert mb={2} status="success">
              <AlertIcon />
              {t("login_page.email_confirmed")}
            </Alert>
          )}
          <Image
            src={images.logoSapienza}
            alignSelf="center"
            height={20}
            alt="Logo sapienza"
          />
          <LoginForm
            onSubmit={onSubmit}
            isLoading={isLoginLoading}
            isDisabled={isConfirmEmailLoading}
            errorMessage={errorMessage}
          />
        </CardBody>
      </Card>
    </BasePage>
  );
}
